import { useRef, useEffect, useState } from 'react';
import { Button } from '@tremor/react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import type { HealthResponse } from '../types';
import type { AuthUser } from '../lib/auth';
import { useAppEnvironment } from '../hooks/useAppEnvironment';

interface HeaderProps {
  health: HealthResponse | null;
  isConnected: boolean;
  user: AuthUser;
  onLogout: () => void;
}

export function Header({ health, isConnected, user, onLogout }: HeaderProps) {
  const isHealthy = health?.status === 'healthy';
  const redisConnected = health?.redis === 'connected';
  const allHealthy = isHealthy && redisConnected && isConnected;
  const location = useLocation();
  const pathname = location.pathname;
  const env = useAppEnvironment();

  // Track initial version to detect updates
  const initialVersionRef = useRef<string | null>(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);

  useEffect(() => {
    if (health?.version) {
      if (initialVersionRef.current === null) {
        // Store the initial version on first load
        initialVersionRef.current = health.version;
      } else if (health.version !== initialVersionRef.current) {
        // Version changed - new deployment detected
        setNewVersionAvailable(true);
      }
    }
  }, [health?.version]);

  const handleRefreshPage = () => {
    window.location.reload();
  };

  const classifiersActive =
    pathname.startsWith('/classifiers') ||
    pathname.startsWith('/bundles') ||
    pathname === '/folder-mapping';

  const documentsActive = pathname.startsWith('/documents') || pathname === '/search';

  const monitoringActive = pathname.startsWith('/monitoring');

  const adminActive = pathname.startsWith('/admin') || pathname === '/settings';

  const statusTitle = `Redis: ${redisConnected ? 'OK' : 'Down'}\nSystem: ${
    isHealthy ? 'OK' : 'Down'
  }\nWebSocket: ${isConnected ? 'OK' : 'Down'}`;

  const initial = (user.name?.trim()?.[0] || user.email?.trim()?.[0] || '?').toUpperCase();
  const showName = user.name && user.name.trim() && user.name.trim() !== user.email;

  return (
    <header className="bg-gray-900 border-b border-gray-800">
      {/* New version banner */}
      {newVersionAvailable && (
        <div className="bg-blue-600 px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm">
              A new version ({health?.version}) is available.
            </span>
          </div>
          <Button
            size="xs"
            onClick={handleRefreshPage}
            className="bg-white text-blue-600 hover:bg-blue-50 border-0"
          >
            Refresh to update
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white hover:text-gray-200 transition-colors">
              HarmCheck Console
            </h1>
            {env?.appEnvironment && <EnvBadge env={env.appEnvironment} />}
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-1 ml-4 pl-4 border-l border-gray-700">
            <NavDropdown
              label="Documents"
              active={documentsActive}
              items={[
                { label: 'List', to: '/documents', active: pathname.startsWith('/documents') },
                { label: 'Advanced Search', to: '/search', active: pathname === '/search' },
              ]}
            />
            <NavDropdown
              label="Classifiers"
              active={classifiersActive}
              items={[
                { label: 'Classifiers', to: '/classifiers', active: pathname.startsWith('/classifiers') },
                { label: 'Bundles', to: '/bundles', active: pathname.startsWith('/bundles') },
                { label: 'Folder Map', to: '/folder-mapping', active: pathname === '/folder-mapping' },
              ]}
            />
            <NavDropdown
              label="Monitoring"
              active={monitoringActive}
              items={[
                { label: 'Overview', to: '/monitoring', active: pathname === '/monitoring' },
                { label: 'API Usage', to: '/monitoring/usage', active: pathname === '/monitoring/usage' },
                { label: 'Outlook HarmCheck', to: '/monitoring/outlook', active: pathname === '/monitoring/outlook' },
              ]}
            />
            <NavDropdown
              label="Admin"
              active={adminActive}
              items={[
                { label: 'Users', to: '/admin/users', active: pathname.startsWith('/admin/users') },
                { label: 'Orgs', to: '/admin/orgs', active: pathname.startsWith('/admin/orgs') },
                { label: 'API Keys', to: '/admin/api-keys', active: pathname.startsWith('/admin/api-keys') },
                { label: 'Audit', to: '/admin/audit', active: pathname === '/admin/audit' },
                { label: 'Logs', to: '/admin/logs', active: pathname === '/admin/logs' },
                { label: 'Environments', to: '/admin/environments', active: pathname.startsWith('/admin/environments') },
                { label: 'Settings', to: '/settings', active: pathname === '/settings' },
              ]}
              footer={
                <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2">
                  <span
                    title={statusTitle}
                    className={`w-2.5 h-2.5 rounded-full ${
                      allHealthy ? 'bg-emerald-500 animate-pulse-dot' : 'bg-red-500'
                    }`}
                  />
                  {health?.version ? (
                    <span>
                      v{health.version}
                      {newVersionAvailable && ' (new)'}
                    </span>
                  ) : (
                    <span>Status</span>
                  )}
                </div>
              }
            />
          </nav>
        </div>

        <Menu as="div" className="relative">
          <MenuButton
            aria-label="Account menu"
            className="h-8 w-8 rounded-full overflow-hidden bg-gray-700 text-white text-sm font-medium flex items-center justify-center hover:ring-2 hover:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 data-[open]:ring-2 data-[open]:ring-blue-500"
          >
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span>{initial}</span>
            )}
          </MenuButton>
          <MenuItems
            anchor="bottom end"
            className="z-50 mt-2 min-w-[220px] bg-gray-900 border border-gray-800 rounded-lg shadow-lg p-1 focus:outline-none"
          >
            <div className="px-3 py-2">
              {showName && (
                <div className="text-gray-200 text-sm font-medium truncate" title={user.name}>
                  {user.name}
                </div>
              )}
              <div className="text-gray-500 text-xs truncate" title={user.email}>
                {user.email}
              </div>
            </div>
            <div className="border-t border-gray-800 my-1" />
            <MenuItem>
              <button
                onClick={onLogout}
                className="w-full text-left px-3 py-2 text-sm rounded text-red-400 data-[focus]:bg-gray-800"
              >
                Logout
              </button>
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
    </header>
  );
}

function EnvBadge({ env }: { env: string }) {
  const normalized = env.toLowerCase();
  const isProd = normalized === 'prod' || normalized === 'production';
  const isStaging = normalized === 'staging';
  const classes = isProd
    ? 'bg-red-900/60 text-red-200 border-red-700'
    : isStaging
      ? 'bg-amber-900/40 text-amber-200 border-amber-700/60'
      : 'bg-gray-800 text-gray-300 border-gray-700';
  return (
    <span
      className={`text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded border ${classes}`}
      title={`Dashboard is connected to the ${env} environment`}
    >
      {env}
    </span>
  );
}

interface NavDropdownItem {
  label: string;
  to: string;
  active: boolean;
}

interface NavDropdownProps {
  label: string;
  active: boolean;
  items: NavDropdownItem[];
  footer?: React.ReactNode;
}

function NavDropdown({ label, active, items, footer }: NavDropdownProps) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={`px-3 py-1.5 rounded text-sm font-medium transition-colors inline-flex items-center gap-1 focus:outline-none ${
          active
            ? 'bg-gray-800 text-white'
            : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
        }`}
      >
        {label}
        <span className="text-xs leading-none">▾</span>
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="z-50 mt-1 min-w-[180px] bg-gray-900 border border-gray-800 rounded-lg shadow-lg p-1 focus:outline-none"
      >
        {items.map((item) => (
          <MenuItem key={item.to}>
            <Link
              to={item.to}
              className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors data-[focus]:bg-gray-800 ${
                item.active ? 'bg-gray-800 text-white' : 'text-gray-300'
              }`}
            >
              {item.label}
            </Link>
          </MenuItem>
        ))}
        {footer && (
          <>
            <div className="border-t border-gray-800 my-1" />
            {footer}
          </>
        )}
      </MenuItems>
    </Menu>
  );
}
